import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

const REG_ACTIVITY_TYPE_LABELS = {
  newUsers: "New Users",
  falloutBidders: "Fallout Bidder",
  revivedBidders: "Revived Bidder",
  doaBidders: "DOA Bidder",
};

const getRegActivityTypeTitle = (type = "") => {
  if (type === "falloutBidders") return "Fallout";
  if (type === "revivedBidders") return "Revived";
  if (type === "doaBidders") return "DOA";
  if (type === "newUsers") return "New User";
  return "Activity";
};

export default function DailyRegistrationActivityTab({
  primary,
  regActivityRangePreset,
  setRegActivityRangePreset,
  regActivityRangeOptions,
  regActivityCustomStartDate,
  setRegActivityCustomStartDate,
  regActivityCustomEndDate,
  setRegActivityCustomEndDate,
  regActivityMeasure,
  setRegActivityMeasure,
  regActivityMeasureOptions,
  regActivityResolvedRangeLabel,
  regActivityTotals,
  regActivityLoading,
  registrationActivityHasData,
  registrationActivityChartData,
  formatNumber2,
  toAbsInt,
  regActivitySortKey,
  regActivitySortDir,
  setRegActivitySortKey,
  setRegActivitySortDir,
  applySortToggle,
  sortedRegistrationActivityRows,
  fetchRegistrationActivityUsers,
}) {
  const renderRegActivityUserCountCell = (row, key) => {
    const count = toAbsInt(row?.[key] || 0);
    if (count <= 0) return formatNumber2(0);
    if (String(row?.bucketType || "daily") !== "daily") return formatNumber2(count);
    return (
      <Button
        size="small"
        variant="outlined"
        onClick={() => fetchRegistrationActivityUsers({ dateKey: row?.dateKey, activityType: key })}
        sx={{ textTransform: "none", fontWeight: 800, minWidth: 64, px: 1.25 }}
      >
        {formatNumber2(count)}
      </Button>
    );
  };

  return (
    <Paper sx={{ borderRadius: 3, p: 2, border: "1px solid rgba(51,57,153,0.10)" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontWeight: 900, color: "#111827" }}>Registration Activity</Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
            Auto-tracked from each new registration during Gmail sync and registration uploads.
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#475569", mt: 0.5 }}>
            Fallout: {">"}30 and {"<"}60 days since prior registration • Revived: {">"}60 and {"<"}90 days • DOA: {">"}90 days
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 200, background: "#fff", borderRadius: 2 }}>
            <InputLabel id="reg-activity-range-label">Date Range</InputLabel>
            <Select
              labelId="reg-activity-range-label"
              value={regActivityRangePreset}
              label="Date Range"
              onChange={(e) => setRegActivityRangePreset(String(e.target.value || "30d"))}
              sx={{ borderRadius: 2 }}
            >
              {regActivityRangeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {regActivityRangePreset === "custom" ? (
            <TextField
              type="date"
              size="small"
              label="Start Date"
              value={regActivityCustomStartDate}
              onChange={(e) => setRegActivityCustomStartDate(String(e.target.value || ""))}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 165, background: "#fff", borderRadius: 2 }}
            />
          ) : null}
          {regActivityRangePreset === "custom" ? (
            <TextField
              type="date"
              size="small"
              label="End Date"
              value={regActivityCustomEndDate}
              onChange={(e) => setRegActivityCustomEndDate(String(e.target.value || ""))}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 165, background: "#fff", borderRadius: 2 }}
            />
          ) : null}

          <FormControl size="small" sx={{ minWidth: 160, background: "#fff", borderRadius: 2 }}>
            <InputLabel id="reg-activity-measure-label">Measure</InputLabel>
            <Select
              labelId="reg-activity-measure-label"
              value={regActivityMeasure}
              label="Measure"
              onChange={(e) => setRegActivityMeasure(String(e.target.value || "daily"))}
              sx={{ borderRadius: 2 }}
            >
              {regActivityMeasureOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ mt: 1.25, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Chip size="small" label={`Range ${regActivityResolvedRangeLabel}`} sx={{ borderRadius: 999, fontWeight: 900 }} />
        <Chip size="small" label={`Current ${formatNumber2(toAbsInt(regActivityTotals?.currentRegistrations || 0))}`} sx={{ borderRadius: 999, fontWeight: 900 }} />
        <Chip size="small" label={`New Users ${formatNumber2(toAbsInt(regActivityTotals?.newUsers || 0))}`} sx={{ borderRadius: 999, fontWeight: 900 }} />
        <Chip size="small" label={`Fallout ${formatNumber2(toAbsInt(regActivityTotals?.falloutBidders || 0))}`} sx={{ borderRadius: 999, fontWeight: 900 }} />
        <Chip size="small" label={`Revived ${formatNumber2(toAbsInt(regActivityTotals?.revivedBidders || 0))}`} sx={{ borderRadius: 999, fontWeight: 900 }} />
        <Chip size="small" label={`DOA ${formatNumber2(toAbsInt(regActivityTotals?.doaBidders || 0))}`} sx={{ borderRadius: 999, fontWeight: 900 }} />
      </Box>

      {regActivityLoading ? (
        <Box sx={{ mt: 1.25 }}>
          <LinearProgress />
        </Box>
      ) : null}

      <Box sx={{ mt: 1.5, width: "100%", height: 280 }}>
        {registrationActivityHasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={registrationActivityChartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="xLabel" tick={{ fill: "#334155", fontSize: 12 }} />
              <YAxis tick={{ fill: "#334155", fontSize: 12 }} />
              <RechartsTooltip
                formatter={(v, name) => [formatNumber2(toAbsInt(v)), String(name || "")]}
                labelFormatter={(label, payload) => {
                  const bucketLabel = String(payload?.[0]?.payload?.bucketLabel || "");
                  const dateKey = String(payload?.[0]?.payload?.dateKey || "");
                  return bucketLabel || dateKey || String(label || "");
                }}
              />
              <Legend />
              <Bar dataKey="currentRegistrations" name="Current Registrations" fill="#2563eb" radius={[6, 6, 0, 0]} />
              <Bar dataKey="newUsers" name="New Users" fill="#d946ef" radius={[6, 6, 0, 0]} />
              <Bar dataKey="falloutBidders" name="Fallout Bidder" fill="#f59e0b" radius={[6, 6, 0, 0]} />
              <Bar dataKey="revivedBidders" name="Revived Bidder" fill="#16a34a" radius={[6, 6, 0, 0]} />
              <Bar dataKey="doaBidders" name="DOA Bidder" fill="#dc2626" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              No registration activity for {regActivityResolvedRangeLabel}.
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid rgba(51,57,153,0.10)", maxHeight: "68vh", overflowY: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ "& .MuiTableCell-root": { backgroundColor: "#f8fafc" } }}>
              <TableCell sx={{ fontWeight: 900, color: primary }}>
                <TableSortLabel
                  active={regActivitySortKey === "dateKey"}
                  direction={regActivitySortKey === "dateKey" ? regActivitySortDir : "asc"}
                  onClick={() => applySortToggle(regActivitySortKey, setRegActivitySortKey, setRegActivitySortDir, "dateKey", "asc")}
                >
                  {regActivityMeasure === "daily"
                    ? "Date"
                    : regActivityMeasure === "weekly"
                      ? "Week"
                      : regActivityMeasure === "monthly"
                        ? "Month"
                        : "Year"}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 900, color: primary }}>
                <TableSortLabel
                  active={regActivitySortKey === "currentRegistrations"}
                  direction={regActivitySortKey === "currentRegistrations" ? regActivitySortDir : "desc"}
                  onClick={() =>
                    applySortToggle(regActivitySortKey, setRegActivitySortKey, setRegActivitySortDir, "currentRegistrations", "desc")
                  }
                >
                  Current Registrations
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 900, color: primary }}>
                <TableSortLabel
                  active={regActivitySortKey === "newUsers"}
                  direction={regActivitySortKey === "newUsers" ? regActivitySortDir : "desc"}
                  onClick={() =>
                    applySortToggle(regActivitySortKey, setRegActivitySortKey, setRegActivitySortDir, "newUsers", "desc")
                  }
                >
                  New Users
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 900, color: primary }}>
                <TableSortLabel
                  active={regActivitySortKey === "falloutBidders"}
                  direction={regActivitySortKey === "falloutBidders" ? regActivitySortDir : "desc"}
                  onClick={() =>
                    applySortToggle(regActivitySortKey, setRegActivitySortKey, setRegActivitySortDir, "falloutBidders", "desc")
                  }
                >
                  Fallout Bidder
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 900, color: primary }}>
                <TableSortLabel
                  active={regActivitySortKey === "revivedBidders"}
                  direction={regActivitySortKey === "revivedBidders" ? regActivitySortDir : "desc"}
                  onClick={() =>
                    applySortToggle(regActivitySortKey, setRegActivitySortKey, setRegActivitySortDir, "revivedBidders", "desc")
                  }
                >
                  Revived Bidder
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 900, color: primary }}>
                <TableSortLabel
                  active={regActivitySortKey === "doaBidders"}
                  direction={regActivitySortKey === "doaBidders" ? regActivitySortDir : "desc"}
                  onClick={() => applySortToggle(regActivitySortKey, setRegActivitySortKey, setRegActivitySortDir, "doaBidders", "desc")}
                >
                  DOA Bidder
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRegistrationActivityRows.map((row, idx) => (
              <TableRow key={String(row?.bucketKey || row?.dateKey || idx)}>
                <TableCell sx={{ color: "#111827", fontWeight: 800 }}>
                  {String(row?.bucketLabel || row?.dateKey || "—")}
                </TableCell>
                <TableCell sx={{ color: "#374151" }}>{formatNumber2(toAbsInt(row?.currentRegistrations || 0))}</TableCell>
                <TableCell sx={{ color: "#374151" }}>{renderRegActivityUserCountCell(row, "newUsers")}</TableCell>
                <TableCell sx={{ color: "#374151" }}>{renderRegActivityUserCountCell(row, "falloutBidders")}</TableCell>
                <TableCell sx={{ color: "#374151" }}>{renderRegActivityUserCountCell(row, "revivedBidders")}</TableCell>
                <TableCell sx={{ color: "#374151" }}>{renderRegActivityUserCountCell(row, "doaBidders")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export function DailyRegistrationActivityUsersDialog({
  open,
  loading,
  onClose,
  modalPaperSx,
  primary,
  regActivityUsersType,
  regActivityUsersDateKey,
  regActivityUsersError,
  regActivityUsersRows,
}) {
  const showRegActivityPreviousAuctionColumn = regActivityUsersType !== "newUsers";

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (loading) return;
        onClose();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
        {`${REG_ACTIVITY_TYPE_LABELS[regActivityUsersType] || "Activity Users"} • ${regActivityUsersDateKey || ""}`}
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#f9fafb" }}>
        {loading ? (
          <Box sx={{ py: 2 }}>
            <LinearProgress />
          </Box>
        ) : null}
        {regActivityUsersError ? (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 1.5 }}>
            {regActivityUsersError}
          </Alert>
        ) : null}

        {!loading && !regActivityUsersError ? (
          <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid rgba(51,57,153,0.10)", maxHeight: "68vh", overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ "& .MuiTableCell-root": { backgroundColor: "#f8fafc" } }}>
                  <TableCell sx={{ fontWeight: 900, color: primary }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 900, color: primary }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 900, color: primary }}>
                    {`${getRegActivityTypeTitle(regActivityUsersType)} Auction / Date`}
                  </TableCell>
                  {showRegActivityPreviousAuctionColumn ? (
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      {`Last Auction Before ${getRegActivityTypeTitle(regActivityUsersType)} / Date`}
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {regActivityUsersRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showRegActivityPreviousAuctionColumn ? 4 : 3} sx={{ color: "#64748b" }}>
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  regActivityUsersRows.map((row) => (
                    <TableRow key={row.id || `${row.email}_${row.detectedAtMs || 0}`}>
                      <TableCell sx={{ color: "#111827", fontWeight: 800 }}>
                        {row.bidderName || row.username || row.company || "—"}
                      </TableCell>
                      <TableCell sx={{ color: "#334155" }}>{row.email || "—"}</TableCell>
                      <TableCell sx={{ color: "#334155" }}>
                        <Typography sx={{ color: "#334155", fontWeight: 800, fontSize: 12.5 }}>
                          {row.auctionName || row.auctionId || "—"}
                        </Typography>
                        <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                          {row.detectedAtMs ? new Date(row.detectedAtMs).toLocaleString() : "—"}
                        </Typography>
                      </TableCell>
                      {showRegActivityPreviousAuctionColumn ? (
                        <TableCell sx={{ color: "#334155" }}>
                          <Typography sx={{ color: "#334155", fontWeight: 800, fontSize: 12.5 }}>
                            {row.previousAuctionName || row.previousAuctionId || "—"}
                          </Typography>
                          <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                            {row.previousRegistrationAtMs ? new Date(row.previousRegistrationAtMs).toLocaleString() : "—"}
                          </Typography>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 2.5, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 3,
            fontWeight: 900,
            border: "1px solid rgba(51,57,153,0.18)",
            color: primary,
            background: "#fff",
            "&:hover": { background: "rgba(99,102,241,0.06)" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
